import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Typography,
  Grid,
  Avatar,
  Paper,
  Divider,
  Box,
  Chip,
  Stack
} from '@mui/material';
import Loader from '../../components/Loader';
import BusinessIcon from '@mui/icons-material/Business';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import WorkOutline from '@mui/icons-material/WorkOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { Check } from '@mui/icons-material';

const ensureAbsoluteUrl = (url) => {
  if (!url) return '';
  return url.match(/^https?:\/\//i) ? url : `https://${url}`;
};

const formatUrl = (url) => {
  if (!url) return 'Not provided';
  try {
    const urlObj = new URL(ensureAbsoluteUrl(url));
    return urlObj.hostname + urlObj.pathname;
  } catch (e) {
    return url.replace(/^https?:\/\/(www\.)?/, '');
  }
};

const isValidUrl = (url) => {
  return url && url.trim() !== '';
};

const DetailField = ({ label, value, link, href }) => (
  <Typography variant="body1" style={{ color: '#555555', marginBottom: '8px' }}>
    <strong>{label}:</strong>{' '}
    {link && value ? (
      <a href={ensureAbsoluteUrl(href)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
        {link}
      </a>
    ) : (
      value || (label === 'Resume' ? 'Not provided' : 'Loading...')
    )}
  </Typography>
);

const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
    {icon}
    <Typography variant="h6" color="primary" style={{ fontWeight: 'bold', marginLeft: '8px' }}>
      {title}
    </Typography>
    <Divider sx={{ ml: 2, flex: 1 }} />
  </Box>
);

const UserProfile = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const token = localStorage.getItem('token')?.trim();
  const navigate = useNavigate();
  const [selectedForms, setSelectedForms] = useState([]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}api/users/profile/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => {
        setUser(response.data);
      })
      .catch(error => {
        console.error('Error fetching user data:', error);
      });
  }, [userId, token]);

  useEffect(() => {
    if (!userId) return;

    const fetchSelectedForms = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}api/users/selected-students/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            }
          }
        );
        setSelectedForms(response.data.forms || []);
      } catch (error) {
        console.error('Error fetching selected forms:', error);
      }
    };

    fetchSelectedForms();
  }, [userId]);

  const formatEnrollmentNumber = (enrollmentNumber, expectedLength = 11) => {
    if (!enrollmentNumber) return enrollmentNumber;
    const strNumber = String(enrollmentNumber);
    if (strNumber.length === expectedLength) return strNumber;
    if (strNumber.length < expectedLength) {
      return strNumber.padStart(expectedLength, '0');
    }
    return strNumber;
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'Loading...';
  };

  // Add these constants for form type styling
  const formTypeLabels = {
    interview: 'Interview',
    test: 'Test',
    hackathon: 'Hackathon',
    general: 'General'
  };

  const formTypeColors = {
    interview: { bg: '#e3f2fd', text: '#0d47a1' },
    test: { bg: '#e8f5e9', text: '#1b5e20' },
    hackathon: { bg: '#fff3e0', text: '#e65100' },
    general: { bg: '#f3e5f5', text: '#4a148c' }
  };

  // Render experience component
  const renderExperience = (exp, index) => (
    <Paper
      key={exp._id || index}
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        borderLeft: '4px solid transparent',
        backgroundColor: '#f9fafc',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          borderLeft: '4px solid #1976d2',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        {exp.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <BusinessIcon fontSize="small" sx={{ mr: 1, color: '#666' }} />
        <Typography variant="body2" color="text.secondary">
          {exp.company} • {exp.employmentType || 'Not specified'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <DateRangeIcon fontSize="small" sx={{ mr: 1, color: '#666' }} />
        <Typography variant="body2" color="text.secondary">
          {exp.startDate ? new Date(exp.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : ''} - {' '}
          {exp.endDate ? new Date(exp.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <LocationOnIcon fontSize="small" sx={{ mr: 1, color: '#666' }} />
        <Typography variant="body2" color="text.secondary">
          {exp.location || 'Remote'}
        </Typography>
      </Box>

      {exp.description && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
            <DescriptionIcon fontSize="small" sx={{ mr: 1, mt: 0.3, color: '#666' }} />
            <Typography variant="subtitle2" fontWeight="bold">
              Description:
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', pl: 4 }}>
            {exp.description}
          </Typography>
        </Box>
      )}

      {renderSkills(exp.skills, '#e3f2fd', '#0d47a1')}

      {renderLinks(exp.links, 'Links:')}
    </Paper>
  );

  // Render certificate component
  const renderCertificate = (cert, index) => (
    <Paper
      key={cert._id || index}
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        borderLeft: '4px solid transparent',
        backgroundColor: '#f9fafc',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          borderLeft: '4px solid #4caf50',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        {cert.name}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <BusinessIcon fontSize="small" sx={{ mr: 1, color: '#666' }} />
        <Typography variant="body2" color="text.secondary">
          {cert.issuingOrganization || 'Not specified'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <DateRangeIcon fontSize="small" sx={{ mr: 1, color: '#666' }} />
        <Typography variant="body2" color="text.secondary">
          Issued: {cert.issueDate ? new Date(cert.issueDate).toLocaleDateString('en-US', {
            month: 'long', year: 'numeric'
          }) : 'Not specified'}
        </Typography>
      </Box>

      {renderSkills(cert.skills, '#e8f5e9', '#1b5e20')}

      {renderLinks(cert.links, 'Certificate Link:', true)}
    </Paper>
  );

  // Reusable function to render skills
  const renderSkills = (skills, bgColor, textColor) => {
    if (!skills || skills.length === 0) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Skills:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {skills.map((skill, i) => (
            <Chip
              key={i}
              label={skill}
              size="small"
              sx={{
                bgcolor: bgColor,
                color: textColor,
                mb: 1
              }}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  // Reusable function to render links
  const renderLinks = (links, title, isCertificate = false) => {
    if (!links || links.length === 0) return null;

    return (
      <Box>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          {title}
        </Typography>
        {links.map((link, i) => (
          link && (
            <Typography
              key={i}
              variant="body2"
              component="a"
              href={ensureAbsoluteUrl(link)}
              target="_blank"
              rel="noopener noreferrer"
              className='w-fit'
              sx={{
                display: 'block',
                color: '#1976d2',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                },
                mb: 0.5
              }}
            >
              {isCertificate ? 'View Certificate' : formatUrl(link)}
            </Typography>
          )
        ))}
      </Box>
    );
  };

  // Add this function to render each application item
  const renderApplicationItem = (form, index) => (
    <Paper
      key={form._id || index}
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        borderLeft: '4px solid transparent',
        backgroundColor: '#f9fafc',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          borderLeft: '4px solid #ff9800',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {form.title}
          </Typography>

          {form.companyName && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BusinessIcon fontSize="small" sx={{ mr: 1, color: '#666' }} />
              <Typography variant="body2" color="text.secondary">
                {form.companyName}
              </Typography>
            </Box>
          )}

          <Chip
            label={formTypeLabels[form.formType] || form.formType}
            size="small"
            sx={{
              bgcolor: formTypeColors[form.formType]?.bg || '#f1f1f1',
              color: formTypeColors[form.formType]?.text || '#666',
              mb: 1
            }}
          />
        </Box>
      </Box>
    </Paper>
  );

  return user ? (
    <>
      <div className="container mx-auto p-5 w-[80%] max-sm:w-[95%] shadow-md mt-5 rounded-md overflow-hidden" style={{ backgroundColor: '#ffffff', color: '#333333', minHeight: '80vh' }}>
        <Grid container spacing={3}>
          {/* Profile Information - REDUCED WIDTH */}
          <Grid item xs={12} md={3} className="text-center">
            <Avatar
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
              alt={user.fullname || 'Profile Picture'}
              src={user.photoURL || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png'}
            />
            <Typography variant="h5" gutterBottom mt={2} style={{ color: '#333333', fontWeight: 'bold' }}>
              {user.fullname || 'Loading...'}
            </Typography>
          </Grid>

          {/* User Details - INCREASED WIDTH */}
          <Grid item xs={12} md={9}>
            <Paper elevation={3} sx={{ padding: 3, borderRadius: 2, backgroundColor: '#ffffff' }}>
              {/* Personal Information */}
              <SectionHeader icon={<PersonIcon color="primary" />} title="Personal Information" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DetailField label="Full Name" value={user.fullname} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Date of Birth" value={formatDate(user.dob)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Nationality" value={user.nationality} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Address" value={user.address} />
                </Grid>
              </Grid>

              {/* Contact Information */}
              <SectionHeader icon={<ContactPhoneIcon color="primary" />} title="Contact Information" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DetailField label="Email" value={user.email} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Contact Number" value={user.contactNumber} />
                </Grid>
              </Grid>

              {/* Academic Information */}
              <SectionHeader icon={<SchoolIcon color="primary" />} title="Academic Information" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DetailField label="Degree" value={user.degree} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Course" value={user.course} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Class" value={user.classes} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="Enrollment Number"
                    value={formatEnrollmentNumber(user.enrollmentNumber)}
                    link={` ${formatEnrollmentNumber(user.enrollmentNumber)}`}
                    href={`https://www.ipuranklist.com/student/${formatEnrollmentNumber(user.enrollmentNumber)}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="CGPA" value={user.cgpa} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Year of Passing" value={user.yearOfPassing} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="12th Percentage" value={user.twelfthPercentage || 'Not provided'} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="10th Percentage" value={user.tenthPercentage || 'Not provided'} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Active Backlogs" value={user.activeBacklogs || 'None'} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Gap Year" value={user.gapYear || 'No gap'} />
                </Grid>
                <Grid item xs={12}>
                  {isValidUrl(user.resumeURL) ? (
                    <DetailField
                      label="Resume"
                      value={user.resumeURL}
                      link="View Resume"
                      href={ensureAbsoluteUrl(user.resumeURL)}
                    />
                  ) : (
                    <DetailField label="Resume" value="Not provided" />
                  )}
                </Grid>
              </Grid>

              {/* Social Profiles */}
              <SectionHeader icon={<LinkIcon color="primary" />} title="Social Profiles" />
              <Grid container spacing={2} columns={1}>
                <Grid item xs={12} md={6}>
                  {isValidUrl(user.linkedin) ? (
                    <DetailField
                      label="LinkedIn"
                      value={user.linkedin}
                      link={formatUrl(user.linkedin)}
                      href={ensureAbsoluteUrl(user.linkedin)}
                    />
                  ) : (
                    <DetailField label="LinkedIn" value="Not provided" />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {isValidUrl(user.github) ? (
                    <DetailField
                      label="GitHub"
                      value={user.github}
                      link={formatUrl(user.github)}
                      href={ensureAbsoluteUrl(user.github)}
                    />
                  ) : (
                    <DetailField label="GitHub" value="Not provided" />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {isValidUrl(user.leetCode) ? (
                    <DetailField
                      label="LeetCode"
                      value={user.leetCode}
                      link={formatUrl(user.leetCode)}
                      href={ensureAbsoluteUrl(user.leetCode)}
                    />
                  ) : (
                    <DetailField label="LeetCode" value="Not provided" />
                  )}
                </Grid>
              </Grid>

              {/* Experience Section */}
              <SectionHeader icon={<WorkOutline color="primary" />} title="Experience" />
              {user.experience && user.experience.length > 0 ? (
                user.experience.map((exp, index) => renderExperience(exp, index))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 3 }}>
                  No experience added yet.
                </Typography>
              )}

              {/* Certificates Section */}
              <SectionHeader icon={<CardMembershipIcon color="primary" />} title="Certificates" />
              {user.certificates && user.certificates.length > 0 ? (
                user.certificates.map((cert, index) => renderCertificate(cert, index))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 3 }}>
                  No certificates added yet.
                </Typography>
              )}

              {/* Selected Section */}
              <SectionHeader icon={<Check color="primary" />} title="Selected" />
              {selectedForms && selectedForms.length > 0 ? (
                selectedForms.map((form, index) => renderApplicationItem(form, index))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 3 }}>
                  No applications added yet.
                </Typography>
              )}
              
              {/* Applications Section */}
              <SectionHeader icon={<AssignmentIcon color="primary" />} title="Applications" />
              {user.appliedForms && user.appliedForms.length > 0 ? (
                user.appliedForms.map((form, index) => renderApplicationItem(form, index))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 3 }}>
                  No applications added yet.
                </Typography>
              )}

            </Paper>
          </Grid>
        </Grid>
      </div>
    </>
  ) : (
    <Loader />
  );
};

export default UserProfile;